import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import useProgressiveImg from './useProgressiveImg'
import {
  Icon,
  ImageStatus,
  ImageContainer,
  ImageProps,
  BaseImageProps,
  BaseImageComponent,
} from './Image'
import { noop } from 'utils'
import { ReactComponent as ImageSlash } from 'icons/image-slash-64.svg'
import { ReactComponent as WarningTriangle } from 'icons/warning-triangle-64.svg'
import { customElementsObserver } from 'modules/analytics'

type BlurUpImageComponentProps = BaseImageProps & { blur: boolean }

const BlurUpImageComponent = styled(
  BaseImageComponent
)<BlurUpImageComponentProps>`
  filter: ${({ blur }) => blur && 'blur(10px)'};
`

interface BlurUpImageProps extends ImageProps {
  thumbnail?: string
  blur?: boolean
}

const BlurUpImage: React.FC<BlurUpImageProps> = ({
  alt,
  className,
  onError = noop,
  onLoad = noop,
  src,
  width,
  height,
  zoomFactor,
  thumbnail,
  srcSet,
}) => {
  const hasSource = !!src
  const [status, setStatus] = useState<ImageStatus>(
    hasSource ? 'loading' : 'no-image'
  )
  const { srcLoaded, blur } = useProgressiveImg(thumbnail ?? '', src ?? '')

  useEffect(() => {
    if (!src || !thumbnail) {
      setStatus('no-image')
    } else {
      setStatus('loading')
    }
  }, [status, src, thumbnail])

  const handleImageLoad: React.ReactEventHandler<HTMLImageElement> = event => {
    setStatus('successful')
    onLoad()
    customElementsObserver.pushEntry({
      element: event.currentTarget,
      identifier: 'blur-up-image',
    })
  }

  const handleImageError = () => {
    setStatus('error')
    onError()
  }

  return (
    <ImageContainer
      aria-busy={blur}
      className={className}
      width={width}
      height={height}>
      {status === 'error' && (
        <Icon as={WarningTriangle} aria-label="Error loading" />
      )}
      {status === 'no-image' && <Icon as={ImageSlash} aria-label="No image" />}
      <BlurUpImageComponent
        alt={alt}
        src={srcLoaded}
        onLoad={handleImageLoad}
        onError={handleImageError}
        zoomFactor={zoomFactor}
        blur={blur}
        srcSet={!blur ? srcSet : undefined}
        elementtiming="blur-up-image"
      />
    </ImageContainer>
  )
}

export default BlurUpImage
